import * as React from 'react';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

interface SearchInputProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  onSearchExecuted?: (query: string) => void;
  value?: string;
  className?: string;
  isLoading?: boolean;
  maintainFocus?: boolean;
  type?: 'search-header' | 'search-results';
}

export const SearchInput = forwardRef<{ focus: () => void }, SearchInputProps>(
  (
    {
      placeholder = 'Search',
      onSearch,
      onSearchExecuted,
      value: initialValue = '',
      className = '',
      isLoading = false,
      maintainFocus = false,
      type = 'search-header'
    },
    ref
  ) => {
    const [query, setQuery] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const { isMobile } = useResponsiveBreakpoints();

    // Expose focus method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }
      }),
      []
    );

    useEffect(() => {
      setQuery(initialValue);
    }, [initialValue]);

    // Maintain focus when maintainFocus prop is true and component re-renders
    useEffect(() => {
      if (maintainFocus && inputRef.current && document.activeElement !== inputRef.current) {
        // Add a small delay to ensure DOM is updated
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    }, [maintainFocus, query]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      if (onSearch) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          onSearch(newQuery);
        }, 500); // 500ms debounce
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && type === 'search-results') {
        e.preventDefault();
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      // If onSearchExecuted is provided, use it (for custom behavior)
      // Otherwise, navigate to search page (default behavior)
      if (onSearchExecuted) {
        onSearchExecuted(query);
        // Maintain focus after search execution if required
        if (maintainFocus && inputRef.current) {
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
      } else if (query.trim()) {
        window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
      }
    };

    const clearSearch = () => {
      setQuery('');
      if (onSearch) {
        onSearch('');
      }
      if (onSearchExecuted) {
        onSearchExecuted('');
      }
      // Maintain focus after clearing if required
      if (maintainFocus && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    };

    return (
      <form
        onSubmit={handleSubmit}
        className={`relative w-full ${className}`}
      >
        <div className='relative flex items-center'>
          <i className='icon icon-search absolute left-3 text-[24px]' />

          <input
            ref={inputRef}
            type='text'
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`w-full pl-12 pr-12 py-4 border border-gray-200 rounded-full ${
              isMobile ? 'b6--medium' : 'b7--medium'
            }`}
          />

          {isLoading && (
            <div className='absolute right-4 w-5 h-5'>
              <svg
                className='animate-spin h-5 w-5 text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}

          <button
            type='button'
            onClick={clearSearch}
            className={`absolute right-3 ${query && !isLoading ? 'block' : 'hidden'}`}
          >
            <span className={`underline text-[#878787] ${isMobile ? 'b8--medium' : 'b7--medium'}`}>Clear</span>
          </button>
        </div>
      </form>
    );
  }
);
